import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";
import { ErrorState, Loading } from "../../components/States";
import { api } from "../../data";
import { useCreatorBase, useInstagram, type IgAccount, type IgShare } from "./data";
import { disconnectInstagram, finishIgConnect, IG_BLOCKS, requestIgSync, revokeIgShare, saveIgConsent, startIgConnect } from "./actions";
import { ConfirmDialog, DemoNote, useCreatorAction, useCreatorContext } from "./ui";
import { pill } from "./model";
import { Dialog, PillView } from "../../components/ui";
import { Icon } from "../../components/Icon";
import { time } from "../../lib/format";
import { toast } from "../../components/Toast";
import { dateOf, tr, useT, type Lang } from "./sprache";
import { InsightsAnsicht } from "../oeffentlich/InsightsAnsicht";
import { periodLabel } from "../oeffentlich/insights-model";
import { igCallbackReason, igSyncErrorText, igSyncStateText } from "./instagram-status";
import { OWN_PERIODS, ownInsightsView, ownMediaSource } from "./insights-eigen";
import { useIgMediaResolver } from "../oeffentlich/insights-media";

/**
 * Instagram verbinden (12_app_creator/instagram.html) mit Verbindungsstand, eigenen Insights, Einwilligung zur Weitergabe
 * an Marken und laufenden Freigaben (TA-130 … TA-139). Seit 0156:
 *  • „Mit Instagram verbinden“ holt die Adresse der Instagram-Anmeldung (Serverfunktion instagram-connect) und leitet dorthin;
 *    ohne Meta-App (Demo) gibt es keinen Knopf, nur den Hinweis (Befund 24: nichts vortäuschen).
 *  • Rückkehr: ?ig=bestaetigen&ticket=… löst das Ticket mit der eigenen Anmeldung ein (die Datenbank stößt den Erst-Abgleich an),
 *    Vollbild-Hinweis bis zum Ende des Erst-Abgleichs mit Notbremse nach 25 s; ?ig=abgebrochen und ?ig=fehler&grund=… als Satz.
 *  • „Aktualisieren“ mit echter Sperre (public.ig_sync_request, 10 Minuten); danach fragt die Seite alle 5 s, höchstens 180 s.
 *  • Eigene Insights mit denselben Bausteinen wie die Ansicht der Marke (insights-eigen.ts, InsightsAnsicht).
 *  • Fehler des letzten Abgleichs als übersetzte Liste (sync_errors).
 */

const POLL_MS = 5_000;
const POLL_MAX_MS = 180_000;
const OVERLAY_MAX_MS = 25_000;

function shareState(s: IgShare, now: string, lang: Lang) {
  if (s.revoked_at) return pill(tr(lang, "Zurückgezogen", "Withdrawn"), "neutral");
  if (s.locked_at) return pill(tr(lang, "Gesperrt", "Locked"), "error");
  if (s.expires_at < now) return pill(tr(lang, "Abgelaufen", "Expired"), "neutral");
  return pill(tr(lang, "Aktiv", "Active"), "success");
}
/** Läuft gerade ein Abgleich? (gestartet und noch nicht beendet) */
function syncRunning(a: IgAccount | null | undefined): boolean {
  if (!a?.sync_started_at) return false;
  return !a.sync_finished_at || Date.parse(a.sync_finished_at) < Date.parse(a.sync_started_at);
}

export function Instagram() {
  const { scope, lang } = useCreatorContext();
  const t = useT();
  const qc = useQueryClient();
  const base = useCreatorBase(scope);
  const ig = useInstagram(scope);
  const [params, setParams] = useSearchParams();
  const { run, busy } = useCreatorAction();
  const [editedConsent, setConsent] = useState<{ allowed: boolean; blocks: string[] } | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [period, setPeriod] = useState("30d");
  // Text erst beim Anzeigen in der aktuellen Sprache: die Rückkehr von Instagram läuft beim ersten Aufbau, bevor die Sprache des Creators geladen ist
  const [notice, setNotice] = useState<{ kind: "info" | "error"; text: (l: Lang) => string } | null>(null);
  const [overlay, setOverlay] = useState(false);
  const [polling, setPolling] = useState<{ since: number; until: number } | null>(null);
  const handled = useRef(false);
  const langRef = useRef(lang);
  langRef.current = lang;
  const fmt = (d: string | null | undefined) => dateOf(d ?? null, lang);
  const uhr = (d: string) => time(d, undefined, lang === "en" ? "en-GB" : "de-DE");

  const consent = editedConsent ?? (ig.data ? { allowed: ig.data.consent?.sharing_allowed ?? false, blocks: ig.data.consent?.allowed_blocks ?? [] } : null);

  // Rückkehr von Instagram: Ticket einlösen (TA-132), Abbruch oder Fehler als Satz; danach die Adresse aufräumen
  const ticket = params.get("ticket");
  const igParam = params.get("ig");
  const grund = params.get("grund");
  useEffect(() => {
    if (handled.current || (!ticket && igParam !== "abgebrochen" && igParam !== "fehler")) return;
    handled.current = true;
    const clean = () => {
      const next = new URLSearchParams(params);
      for (const k of ["ticket", "ig", "grund"]) next.delete(k);
      setParams(next, { replace: true });
    };
    if (ticket) {
      setOverlay(true);
      void run("verbinden", () => finishIgConnect(api, ticket)).then((r) => {
        if (r) toast(tr(langRef.current, "Instagram ist verbunden.", "Instagram is connected."));
        if (r?.sync === "started") setPolling({ since: Date.now(), until: Date.now() + POLL_MAX_MS });
        else setOverlay(false);
      }).finally(clean);
    } else if (igParam === "abgebrochen") {
      setNotice({ kind: "info", text: (l) => tr(l, "Du hast die Anmeldung bei Instagram abgebrochen. Es wurde nichts verbunden.", "You cancelled the Instagram login. Nothing was connected.") });
      clean();
    } else {
      setNotice({ kind: "error", text: (l) => igCallbackReason(grund, l) });
      clean();
    }
  }, [ticket, igParam, grund, params, setParams, run, lang]);

  // Fortschritt: alle 5 s neu laden, höchstens 180 s; fertig, sobald der Abgleich nicht mehr läuft
  useEffect(() => {
    if (!polling) return;
    const id = setInterval(() => {
      if (Date.now() > polling.until) {
        setPolling(null);
        setOverlay(false);
        setNotice({ kind: "info", text: (l) => tr(l, "Der Abgleich dauert länger als üblich. Die Zahlen erscheinen, sobald er fertig ist.", "The sync is taking longer than usual. The numbers appear as soon as it is done.") });
        return;
      }
      void qc.invalidateQueries({ queryKey: ["creator", scope.talentId, "instagram"] });
    }, POLL_MS);
    return () => clearInterval(id);
  }, [polling, qc, scope.talentId, lang]);
  useEffect(() => {
    if (!polling || ig.dataUpdatedAt <= polling.since) return;
    if (!syncRunning(ig.data?.account)) {
      setPolling(null);
      setOverlay(false);
      // Der Hinweis „Abgleich läuft“ darf nach dem Ende nicht stehen bleiben
      setNotice((n) => (n?.kind === "info" ? { kind: "info", text: (l) => tr(l, "Der Abgleich ist fertig. Die Zahlen sind aktuell.", "The sync is done. The numbers are up to date.") } : n));
    }
  }, [polling, ig.data, ig.dataUpdatedAt, lang]);
  // Notbremse: der Vollbild-Hinweis schließt spätestens nach 25 s (der Abgleich läuft im Hintergrund weiter)
  useEffect(() => {
    if (!overlay) return;
    const id = setTimeout(() => setOverlay(false), OVERLAY_MAX_MS);
    return () => clearTimeout(id);
  }, [overlay]);
  // 0429: eigene Bilder (privater Bucket ig-media) als signierte Adressen, gebündelt in einer Anfrage
  const ownMedia = useIgMediaResolver(ownMediaSource(ig.data));

  if (base.error || ig.error) return <ErrorState error={base.error ?? ig.error} retry={() => { void base.refetch(); void ig.refetch(); }} />;
  if (base.isPending || ig.isPending || !base.data || !ig.data || !consent) return <Loading />;

  const talent = base.data.talent;
  const d = ig.data;
  const account = d.account;
  const now = new Date().toISOString();
  const canConnect = !!api.instagramConnect;
  const running = !!polling || syncRunning(account);
  const view = account ? ownInsightsView(d, period, now.slice(0, 10), lang, ownMedia) : null;
  const errors = account?.sync_errors ?? [];
  const brandName = (id: string | null) => (id ? base.data!.brands.get(id)?.name ?? null : null);
  const blockLabel = (key: string) => {
    const bl = IG_BLOCKS.find((x) => x.key === key);
    return bl ? tr(lang, bl.label, bl.en) : key;
  };
  const connectNote = api.mode === "demo"
    ? t("In der Demo gibt es keine Verbindung zu Instagram. Im Betrieb startest du hier die offizielle Freigabe bei Instagram.", "There is no connection to Instagram in the demo. In live use you start the official Instagram authorisation here.")
    : t("Die Anmeldung bei Instagram richtet der Betrieb gerade ein. Sobald sie steht, startest du hier die offizielle Freigabe.", "The Instagram login is being set up. As soon as it is ready, you start the official authorisation here.");
  const connect = () => void run("verbinden-start", async () => {
    const url = await startIgConnect(api, scope.talentId);
    if (url) window.location.assign(url);
    return url;
  });
  const refresh = () => void run("aktualisieren", () => requestIgSync(api, scope.talentId)).then((r) => {
    if (!r) return;
    setNotice({ kind: r.state === "expired" ? "error" : "info", text: (l) => igSyncStateText(r.state, r.retry_after, l) });
    if (r.state === "started" || r.state === "running" || r.state === "first_sync") setPolling({ since: Date.now(), until: Date.now() + POLL_MAX_MS });
  });

  // Ohne Freischaltung durch die Agentur: keine Seite mit gesperrten Knöpfen, nur der Hinweis
  if (!talent.ig_enabled && !account) {
    return (
      <main id="main" className="content tk-cr tk-cr-instagram">
        {/* Jede Seite hat eine Überschrift erster Ordnung, auch ohne Freischaltung (SICHT-CREATOR-09) */}
        <div className="pagehead"><div><div className="eyebrow">{t("Dein Creator-Portal", "Your creator portal")}</div><h1>Instagram</h1></div></div>
        <div className="panel stack tk-empty" role="status">
          <h3>{t("Instagram ist für dein Profil nicht freigeschaltet.", "Instagram is not enabled for your profile.")}</h3>
          <p>{t("Deine Agentur kann die Verbindung freischalten. Danach verbindest du hier dein professionelles Instagram-Konto.", "Your agency can enable the connection. Afterwards you connect your professional Instagram account here.")}</p>
          <Link className="btn" to="/creator/profil">{t("Zu deinem Profil", "Go to your profile")}</Link>
        </div>
      </main>
    );
  }

  return (
    <main id="main" className="content tk-cr tk-cr-instagram">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{t("Dein Creator-Portal", "Your creator portal")}</div>
          <h1>{account ? t("Dein Instagram", "Your Instagram") : t("Instagram verbinden", "Connect Instagram")}</h1>
          <p></p>
        </div>
        <Link className="btn" to="/creator/profil">{t("Zu deinem Profil", "Go to your profile")}</Link>
      </div>

      {notice && <p className={notice.kind === "error" ? "tk-error" : "notice"} role={notice.kind === "error" ? "alert" : "status"}>{notice.text(lang)}</p>}

      {!account ? (
        <section className="panel stack">
          <h3>{t("Deine Zahlen. Deine Freigabe.", "Your numbers. Your consent.")}</h3>
          <p>{t("Verbinde dein professionelles Instagram-Konto, damit deine Agentur aktuelle Insights sehen kann.", "Connect your professional Instagram account so your agency can see current insights.")}</p>
          <ul>
            <li>{t("Profil- und Reichweitendaten", "Profile and reach data")}</li>
            <li>{t("Verfügbare Content-Kennzahlen", "Available content metrics")}</li>
            <li>{t("Verfügbare Zielgruppen-Daten", "Available audience data")}</li>
          </ul>
          <p>{t("Welche Kennzahlen verfügbar sind, hängt von Kontotyp und API-Berechtigungen ab. Du kannst die Verbindung jederzeit trennen.", "Which metrics are available depends on the account type and API permissions. You can disconnect at any time.")}</p>
          {canConnect ? (
            <div className="row">
              <button className="primary" id="ig-verbinden" onClick={connect} disabled={busy === "verbinden-start"}>
                {busy === "verbinden-start" ? t("Instagram wird geöffnet …", "Opening Instagram …") : t("Mit Instagram verbinden", "Connect with Instagram")}
              </button>
            </div>
          ) : (
            <p className="notice" id="ig-verbinden">{connectNote}</p>
          )}
          <small>{t("Du meldest dich direkt bei Instagram an. CreatorOffice fragt nie nach deinem Passwort und liest nur Profil und Insights, keine Nachrichten.", "You sign in directly with Instagram. CreatorOffice never asks for your password and only reads your profile and insights, no messages.")}</small>
        </section>
      ) : (
        <section className="panel stack tk-ig-verbindung" aria-label="Instagram">
          {/* D-2 (Inhaber 21.10.2026): eine Zeile statt Überschrift und Zahlenblock; Follower und Beiträge stehen in den Insights
              darunter. Die englischen Beschriftungen bleiben wie im Meta-Prüfvideo: „Connected“, „Refresh“, „Disconnect“. */}
          <div className="tk-ig-zeile">
            <span className="tk-ig-bild" aria-hidden="true">{(account.username || "?").slice(0, 1).toUpperCase()}</span>
            <span className="tk-ig-wer">
              <strong className="tk-ig-name" title={account.name ?? undefined}>@{account.username}</strong>
              <span className={`tk-ig-stand tk-ig-stand-${account.status === "active" ? "ok" : account.status === "expired" ? "warnung" : "fehler"}`}>
                {account.status === "active" ? <><span aria-hidden="true">✓ </span>{t("Verbunden", "Connected")}</> : account.status === "expired" ? t("Anmeldung abgelaufen", "Login expired") : t("Widerrufen", "Revoked")}
              </span>
              <small className="tk-ig-zeit" title={t(`Verbunden seit ${fmt(account.connected_at)}`, `Connected since ${fmt(account.connected_at)}`)}>
                {running
                  ? t("Abgleich läuft", "Sync running")
                  : account.last_sync_at ? t(`Stand ${fmt(account.last_sync_at)}, ${uhr(account.last_sync_at)}`, `As of ${fmt(account.last_sync_at)}, ${uhr(account.last_sync_at)}`) : t("Erstabgleich läuft", "first sync running")}
              </small>
            </span>
            <span className="tk-ig-aktionen">
              {account.status === "active" && (
                <button type="button" className={`tk-ig-knopf tk-ig-aktualisieren${running ? " dreht" : ""}`} onClick={refresh} disabled={busy === "aktualisieren" || running} aria-busy={running || busy === "aktualisieren"}>
                  <Icon name="refresh" />{t("Aktualisieren", "Refresh")}
                </button>
              )}
              {account.status !== "active" && canConnect && (
                <button className="primary tk-ig-knopf" onClick={connect} disabled={busy === "verbinden-start"}>{t("Neu verbinden", "Reconnect")}</button>
              )}
              {/* ruhig; rot erst in der Rückfrage */}
              <button type="button" className="tk-ig-knopf tk-ig-trennen" onClick={() => setConfirmDisconnect(true)}>{t("Trennen", "Disconnect")}</button>
            </span>
          </div>
          {errors.length > 0 ? (
            <div className="tk-error" role="status">
              <strong>{t("Beim letzten Abgleich hat nicht alles geklappt:", "Not everything worked during the last sync:")}</strong>
              <ul>{errors.map((e, i) => <li key={`${e.code}-${e.step}-${i}`}>{igSyncErrorText(e.code, e.step, lang)}</li>)}</ul>
            </div>
          ) : account.last_error ? (
            <p className="tk-error">{t("Letzter Abgleich fehlgeschlagen", "Last sync failed")}: {account.last_error}</p>
          ) : null}
          {account.status === "expired" && (
            <p className="notice">{t("Instagram hat die Anmeldung beendet oder sie ist nach 60 Tagen abgelaufen. Verbinde dein Konto neu, damit die Zahlen wieder aktuell werden.", "Instagram ended the login or it expired after 60 days. Reconnect your account so the numbers are up to date again.")}</p>
          )}
        </section>
      )}

      {account && view && (
        <section className="stack tk-ig-eigen" aria-label={t("Deine Insights", "Your insights")}>
          {/* Zeitraum als Pille im Kopf der Insights, wie „Letzte 30 Tage“ in der Instagram-App (0164) */}
          <InsightsAnsicht view={view} embedded own periodControl={
            <select aria-label={t("Zeitraum", "Period")} value={period} onChange={(e) => setPeriod(e.target.value)}>
              {OWN_PERIODS.map((p) => <option key={p} value={p}>{periodLabel(p, lang)}</option>)}
            </select>
          } />
        </section>
      )}

      <section className="panel stack">
        <h3>{t("Weitergabe an Marken", "Sharing with brands")}</h3>
        <p>{t("Du entscheidest, ob deine Agentur ausgewählte Kennzahlen per Link an Marken weitergeben darf. Ohne deine Zustimmung ist keine Freigabe möglich.", "You decide whether your agency may share selected metrics with brands via a link. No sharing is possible without your consent.")}</p>
        <label className="row tk-check">
          <input type="checkbox" checked={consent.allowed} onChange={(e) => setConsent({ ...consent, allowed: e.target.checked })} />
          {t("Meine Agentur darf ausgewählte Kennzahlen an Marken weitergeben.", "My agency may share selected metrics with brands.")}
        </label>
        {consent.allowed && (
          <fieldset className="stack tk-gap-sm">
            <legend>{t("Diese Bereiche darf sie teilen", "It may share these areas")}</legend>
            <div className="tk-checks">
              {IG_BLOCKS.map((bl) => (
                <label key={bl.key}>
                  <input
                    type="checkbox"
                    checked={consent.blocks.includes(bl.key)}
                    onChange={(e) => setConsent({ ...consent, blocks: e.target.checked ? [...consent.blocks, bl.key] : consent.blocks.filter((x) => x !== bl.key) })}
                  />
                  {tr(lang, bl.label, bl.en)}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        <div className="row">
          <button
            className="primary"
            disabled={busy === "einwilligung" || (consent.allowed && consent.blocks.length === 0)}
            onClick={() => void run("einwilligung", () => saveIgConsent(api, scope, consent.allowed, consent.blocks, d.consent), t("Deine Auswahl ist gespeichert.", "Your selection has been saved."))}
          >
            {t("Auswahl speichern", "Save selection")}
          </button>
          {consent.allowed && consent.blocks.length === 0 && <small>{t("Wähle mindestens einen Bereich.", "Choose at least one area.")}</small>}
          {d.consent && <small>{t(`Zuletzt geändert am ${fmt(d.consent.updated_at)}`, `Last changed on ${fmt(d.consent.updated_at)}`)}</small>}
        </div>
      </section>

      <section className="panel stack">
        <h3>{t("Freigaben an Marken", "Shares with brands")}</h3>
        {d.shares.length === 0 && <p>{t("Deine Agentur hat noch keine Kennzahlen an eine Marke freigegeben.", "Your agency has not shared any metrics with a brand yet.")}</p>}
        {d.shares.map((s) => {
          const st = shareState(s, now, lang);
          const active = !s.revoked_at && !s.locked_at && s.expires_at >= now;
          return (
            <div key={s.id} className="task">
              <div>
                <strong>{brandName(s.brand_id) ?? s.recipient_email}</strong>
                <p>{s.blocks.map(blockLabel).join(", ")} · {periodLabel(s.period, lang)} · {t(`gültig bis ${fmt(s.expires_at)}`, `valid until ${fmt(s.expires_at)}`)}</p>
              </div>
              <div className="row">
                <PillView pill={st} />
                {active && <button onClick={() => void run(`widerruf-${s.id}`, () => revokeIgShare(api, s.id), t("Freigabe zurückgezogen.", "Share withdrawn."))} disabled={busy === `widerruf-${s.id}`}>{t("Zurückziehen", "Withdraw")}</button>}
              </div>
            </div>
          );
        })}
      </section>

      <ConfirmDialog
        open={confirmDisconnect}
        title={t("Verbindung trennen?", "Disconnect?")}
        text={t("CreatorOffice löscht deinen Zugangsschlüssel und alle Instagram-Daten sofort. Laufende Freigaben an Marken enden.", "CreatorOffice deletes your access key and all Instagram data immediately. Running shares with brands end.")}
        confirm={t("Verbindung trennen", "Disconnect")}
        danger
        busy={busy === "trennen"}
        onConfirm={() => void run("trennen", () => disconnectInstagram(api, scope), t("Instagram ist getrennt. Deine Daten sind gelöscht.", "Instagram is disconnected. Your data has been deleted.")).then((ok) => ok && setConfirmDisconnect(false))}
        onClose={() => setConfirmDisconnect(false)}
      />
      {/* Vollbild-Hinweis nach der Rückkehr von Instagram (Notbremse nach 25 s, der Abgleich läuft weiter) */}
      <Dialog open={overlay} onClose={() => setOverlay(false)} title={t("Instagram wird verbunden", "Connecting Instagram")}>
        <p role="status">{busy === "verbinden"
          ? t("Wir schließen die Verbindung ab …", "Finishing the connection …")
          : t("Wir holen jetzt deine ersten Zahlen von Instagram. Das dauert bis zu zwei Minuten; du kannst dieses Fenster schließen, der Abgleich läuft weiter.", "We are now fetching your first numbers from Instagram. This takes up to two minutes; you can close this window, the sync keeps running.")}</p>
        <div className="row"><button onClick={() => setOverlay(false)}>{t("Schließen", "Close")}</button></div>
      </Dialog>
      <DemoNote />
    </main>
  );
}
